/**
 * AI drafting (US-D「AI 真实起草」) types.
 *
 * Field shapes mirror the backend DraftResponse / DraftCreate schemas
 * (app/modules/jerry/schemas.py). The API layer camel-cases the JSON, so
 * interfaces here use camelCase — EXCEPT {@link DraftCitation}, whose keys
 * come verbatim from the Lobster `doc-draft` skill and are stored/returned
 * as a raw JSONB list the backend does NOT key-transform (same exception as
 * `Message.citations`, see types/index.ts). Do not "correct" them to
 * camelCase — doing so silently breaks citation rendering.
 */

/** Drafting instruction dimension — one of the doc-draft skill's frozen input values. */
export type DraftDocType = "contract" | "legal_opinion" | "letter" | "other";

/** Chinese display labels for drafting doc types (docs/glossary.md). */
export const DRAFT_DOC_TYPE_LABELS: Record<DraftDocType, string> = {
  contract: "合同",
  legal_opinion: "法律意见书",
  letter: "函件",
  other: "其他",
};

/** Selectable doc-type options for the drafting form. */
export const DRAFT_DOC_TYPE_OPTIONS: { value: DraftDocType; label: string }[] = (
  Object.entries(DRAFT_DOC_TYPE_LABELS) as [DraftDocType, string][]
).map(([value, label]) => ({ value, label }));

/** 工作台草稿的缺省标题——对照后端常量 `app/modules/jerry/enums.py` 的
 * DEFAULT_DRAFT_TITLE。创建流程简化（2026-08-28）后新建草稿一律以此标题
 * 起步，AI 首稿产出后由服务端自动命名；前端据此值判断"仍未命名"以展示
 * 自动命名提示。 */
export const DEFAULT_DRAFT_TITLE = "未命名草稿";

/** Drafting record lifecycle (DraftStatus): pending → running → 终态三选一. */
export type DraftStatus = "pending" | "running" | "succeeded" | "failed" | "cancelled";

/** Chinese display labels for drafting record statuses. */
export const DRAFT_STATUS_LABELS: Record<DraftStatus, string> = {
  pending: "待生成",
  running: "生成中",
  succeeded: "成功",
  failed: "失败",
  cancelled: "已取消",
};

/** Non-terminal statuses — the frontend keeps polling while a draft is in one of these. */
export const DRAFT_NON_TERMINAL_STATUSES: DraftStatus[] = ["pending", "running"];

/** Terminal statuses — polling stops once a draft reaches one of these. */
export const DRAFT_TERMINAL_STATUSES: DraftStatus[] = ["succeeded", "failed", "cancelled"];

/**
 * A single citation the doc-draft skill attached to generated text.
 *
 * IMPORTANT — the snake_case keys are intentional (see file header). The
 * skill emits `{document_id, snippet, location}` and jerry stores it
 * verbatim (JerryDraft.citations); `location` is a free-text page/paragraph
 * locator string, or null when the skill could not place one.
 */
/** 一份参考文档在本次起草中的覆盖方式（引擎 doc-draft 的 coverage_notes 元素，snake_case 原样）。 */
export interface DraftCoverageNote {
  document_id: string;
  /** full_text / retrieval_oversize / retrieval_no_text */
  mode: string;
  chars?: number;
  chunk_count?: number;
  note: string;
}

export interface DraftCitation {
  document_id: string;
  snippet: string;
  location: string | null;
}

/** Upper bound on selectable context documents per draft (mirrors MAX_CHAT_DOCUMENT_IDS). */
export const DRAFT_CONTEXT_DOCUMENT_MAX = 20;

/** Max length of the drafting instructions field (DraftCreate.instructions). */
export const DRAFT_INSTRUCTIONS_MAX_LENGTH = 5000;

/** Max length of the save-as-document document name (DraftSaveAsDocumentRequest.documentName). */
export const DRAFT_DOCUMENT_NAME_MAX_LENGTH = 300;

/** A persisted drafting record (DraftResponse). */
export interface Draft {
  id: string;
  matterId: string;
  createdBy: string;
  docType: DraftDocType;
  instructions: string;
  contextDocumentIds: string[];
  status: DraftStatus;
  /**
   * Already-localized failure message (e.g. "AI 起草暂不可用"、"生成超时，
   * 请重试"、"起草结果解析失败，请重试") — the backend maps the engine's
   * error code to Chinese text before persisting, so the frontend renders
   * this string as-is and never re-derives it from an error code.
   */
  failureReason: string | null;
  /** Populated only once status reaches "succeeded"; null otherwise (no half-written product). */
  body: string | null;
  citations: DraftCitation[] | null;
  /** 1A-4(c)：每份参考文档的覆盖方式（整份 / 超长降级检索 / 取不到全文降级）。
   * 引擎原样下发；旧记录与旧引擎为 null，不渲染。 */
  coverageNotes: DraftCoverageNote[] | null;
  /** BR-2: insufficient citations → draft still renders, but is flagged non-deliverable. */
  isNonDeliverable: boolean;
  modelId: string | null;
  provider: string | null;
  inputTokens: number | null;
  outputTokens: number | null;
  totalTokens: number | null;
  latencyMs: number | null;
  lobsterTaskId: string | null;
  /** Set once the lawyer "另存为文档" (US-D02). */
  savedDocumentId: string | null;
  /** Set only on the new record a retry (BR-10) creates. */
  retryOfId: string | null;
  createdAt: string;
  updatedAt: string;
}

/** Payload to create (and immediately run) a draft (DraftCreate). */
export interface DraftCreateRequest {
  docType: DraftDocType;
  instructions: string;
  contextDocumentIds?: string[];
  retryOfId?: string;
}

/** Payload to save a draft's (possibly hand-edited) body as a document (DraftSaveAsDocumentRequest). */
export interface DraftSaveAsDocumentRequest {
  documentName: string;
  body: string;
}
